import type { OpencodePartData } from "schemas/generated/agentTools/opencode";
import type {
  AgentSessionActivityPhaseHeader,
  AgentSessionChainItem,
  AgentSessionItem,
} from "../tools/models/agent-session";
import type { AgentToolModel } from "../tools/models/agent-tool";

import {
  messagePartDeltaEventSchema,
  opencodePartDataSchema,
} from "schemas/agentTools/opencode";
import { z } from "zod";

import {
  buildActivityPhases,
  getActivityPhaseHeaderFromTextPart,
  getAgentSessionText,
} from "../tools/models/agent-session";
import { getAgentToolModels } from "../tools/models/agent-tool";

export type SessionStreamSnapshot = {
  items: readonly AgentSessionItem[];
  text: string;
  tools: AgentToolModel[];
  todos: SessionStreamTodo[];
};

type SessionStreamTodo = {
  id: string;
  content: string;
  status: string;
  priority: string;
};

type SessionStreamPart = Extract<OpencodePartData, { type: "text" | "tool" }>;
type SessionStreamTextPart = Extract<SessionStreamPart, { type: "text" }>;
type MessageRole = "assistant" | "user";
const messageRoleSchema = z.object({
  id: z.string(),
  role: z.enum(["assistant", "user"]),
});

interface SessionStreamSubscriber {
  resolve: (value: SessionStreamSnapshot) => void;
  reject: (reason: Error) => void;
  onSnapshot?: (snapshot: SessionStreamSnapshot) => void;
}

interface SessionWaiter {
  sessionId: string;
  subscribers: SessionStreamSubscriber[];
  completionTimer: ReturnType<typeof setTimeout> | null;
  isStatusCompletionRequested: boolean;
  messageRoles: Map<string, MessageRole>;
  partIndices: Map<string, number>;
  parts: SessionStreamPart[];
  submittedPromptTexts: string[];
}

const IDLE_SETTLE_TIMEOUT_MS = 500;

function getToolModelsByPartCallId(
  waiter: SessionWaiter,
): Map<string, AgentToolModel[]> {
  const toolParts = waiter.parts.flatMap((part) => {
    return part.type === "tool" ? [part] : [];
  });
  const toolModelsByPartCallId = new Map<string, AgentToolModel[]>();
  for (const toolPart of toolParts) {
    toolModelsByPartCallId.set(
      toolPart.callID,
      getAgentToolModels({
        currentToolPart: toolPart,
        messageParts: [],
        sessionToolParts: toolParts,
      }),
    );
  }
  return toolModelsByPartCallId;
}

function getSessionStreamItems({
  toolModelsByPartCallId,
  waiter,
}: {
  toolModelsByPartCallId: ReadonlyMap<string, AgentToolModel[]>;
  waiter: SessionWaiter;
}): AgentSessionItem[] {
  const items: AgentSessionItem[] = [];

  // Collect headers from synthetic text parts so the stream snapshot builds the same phases the editor renders.
  const headersByCallId = new Map<string, AgentSessionActivityPhaseHeader>();
  for (const part of waiter.parts) {
    if (part.type !== "text") {
      continue;
    }
    const header = getActivityPhaseHeaderFromTextPart({ part });
    if (header) {
      headersByCallId.set(header.sectionStartCallId, header);
    }
  }

  let chainItems: AgentSessionChainItem[] = [];

  function flushChain() {
    if (chainItems.length === 0) {
      return;
    }

    items.push({
      type: "chain",
      items: chainItems,
      phases: buildActivityPhases({ headersByCallId, items: chainItems }),
    });
    chainItems = [];
  }

  for (const part of waiter.parts) {
    if (part.type === "tool") {
      for (const tool of toolModelsByPartCallId.get(part.callID) ?? []) {
        if (tool.content.kind === "integrationStatus") {
          flushChain();
          items.push({ type: "standaloneTool", tool });
        } else {
          chainItems.push({ type: "tool", tool });
        }
      }
      continue;
    }

    if (!isAssistantText({ part, waiter }) || part.text.trim().length === 0) {
      continue;
    }

    const textItem = { type: "text" as const, id: part.id, text: part.text };
    if (chainItems.length > 0) {
      chainItems.push(textItem);
      continue;
    }

    items.push(textItem);
  }

  flushChain();
  return items;
}

function getSessionSnapshot(waiter: SessionWaiter): SessionStreamSnapshot {
  const toolModelsByPartCallId = getToolModelsByPartCallId(waiter);
  const tools = [...toolModelsByPartCallId.values()].flat();
  const items = getSessionStreamItems({ toolModelsByPartCallId, waiter });

  return {
    items,
    text: getAgentSessionText({ items }),
    todos: [],
    tools,
  };
}

function getScopedPartKey(part: SessionStreamPart): string {
  return `${part.sessionID}:${part.id}`;
}

function upsertSessionPart({
  part,
  partIndices,
  parts,
}: {
  part: SessionStreamPart;
  partIndices: Map<string, number>;
  parts: SessionStreamPart[];
}) {
  const partKey = getScopedPartKey(part);
  if (partIndices.has(partKey)) {
    const existingPartIndex = partIndices.get(partKey)!;
    parts[existingPartIndex] = part;
    return;
  }
  partIndices.set(partKey, parts.length);
  parts.push(part);
}

function hasSessionOutput(waiter: SessionWaiter): boolean {
  return waiter.parts.some((part) => {
    return (
      part.type === "tool" ||
      (part.type === "text" &&
        part.text.length > 0 &&
        isAssistantText({ part, waiter }))
    );
  });
}

function getMessageRole(input: unknown): {
  messageId: string;
  role: MessageRole;
} | null {
  const parsed = messageRoleSchema.safeParse(input).data;
  return parsed ? { messageId: parsed.id, role: parsed.role } : null;
}

function isAssistantText({
  part,
  waiter,
}: {
  part: SessionStreamTextPart;
  waiter: SessionWaiter;
}): boolean {
  const messageRole = waiter.messageRoles.get(part.messageID);
  if (messageRole) {
    return messageRole === "assistant";
  }

  return Boolean(part.time);
}

function clearCompletionTimer(waiter: SessionWaiter) {
  if (!waiter.completionTimer) {
    return;
  }
  clearTimeout(waiter.completionTimer);
  waiter.completionTimer = null;
}

export class SessionStreamManager {
  private readonly waiters = new Map<string, SessionWaiter>();

  private publishSnapshot(waiter: SessionWaiter) {
    const snapshot = getSessionSnapshot(waiter);
    for (const subscriber of waiter.subscribers) {
      subscriber.onSnapshot?.(snapshot);
    }
  }

  private applyTextDelta({
    waiter,
    properties,
  }: {
    waiter: SessionWaiter;
    properties: z.infer<typeof messagePartDeltaEventSchema>["properties"];
  }) {
    const partKey = `${properties.sessionID}:${properties.partID}`;
    const existingIndex = waiter.partIndices.get(partKey);
    if (existingIndex !== undefined) {
      const existing = waiter.parts[existingIndex];
      if (!existing || existing.type !== "text") {
        return;
      }
      waiter.parts[existingIndex] = {
        ...existing,
        text: existing.text + properties.delta,
      };
    } else {
      // A delta can beat its part's created event. Deltas only stream for
      // assistant generation, so seed with a start time — that is the signal
      // isAssistantText falls back to when the message role is unknown. The
      // eventual whole-part upsert replaces this seed.
      upsertSessionPart({
        part: {
          id: properties.partID,
          sessionID: properties.sessionID,
          messageID: properties.messageID,
          type: "text",
          text: properties.delta,
          time: { start: Date.now() },
        },
        partIndices: waiter.partIndices,
        parts: waiter.parts,
      });
    }
    this.publishSnapshot(waiter);
  }

  private finishNow(waiter: SessionWaiter) {
    clearCompletionTimer(waiter);
    const snapshot = getSessionSnapshot(waiter);
    for (const subscriber of waiter.subscribers) {
      subscriber.resolve(snapshot);
    }
    this.waiters.delete(waiter.sessionId);
  }

  private scheduleIdleCompletion(waiter: SessionWaiter) {
    waiter.isStatusCompletionRequested = true;
    if (!hasSessionOutput(waiter)) {
      return;
    }

    clearCompletionTimer(waiter);
    waiter.completionTimer = setTimeout(() => {
      if (this.waiters.get(waiter.sessionId) === waiter) {
        this.finishNow(waiter);
      }
    }, IDLE_SETTLE_TIMEOUT_MS);
  }

  private markSubmittedPromptAsUserText({
    part,
    waiter,
  }: {
    part: SessionStreamTextPart;
    waiter: SessionWaiter;
  }) {
    if (waiter.messageRoles.has(part.messageID) || !part.time) {
      return;
    }

    const submittedPromptText = waiter.submittedPromptTexts[0];
    if (!submittedPromptText || part.text !== submittedPromptText) {
      return;
    }

    waiter.messageRoles.set(part.messageID, "user");
    waiter.submittedPromptTexts.shift();
  }

  createStream({
    sessionId,
    onSnapshot,
    submittedPromptTexts = [],
  }: {
    sessionId: string;
    onSnapshot?: (snapshot: SessionStreamSnapshot) => void;
    submittedPromptTexts?: string[];
  }): Promise<SessionStreamSnapshot> {
    return new Promise<SessionStreamSnapshot>((resolve, reject) => {
      const subscriber: SessionStreamSubscriber = {
        resolve,
        reject,
        onSnapshot,
      };
      const existing = this.waiters.get(sessionId);
      if (existing) {
        existing.subscribers.push(subscriber);
        existing.submittedPromptTexts.push(...submittedPromptTexts);
        return;
      }
      this.waiters.set(sessionId, {
        sessionId,
        subscribers: [subscriber],
        completionTimer: null,
        isStatusCompletionRequested: false,
        messageRoles: new Map<string, MessageRole>(),
        partIndices: new Map<string, number>(),
        parts: [],
        submittedPromptTexts: [...submittedPromptTexts],
      });
    });
  }

  routeEvent(message: Record<string, unknown>) {
    if (message.type !== "opencode-event") {
      return;
    }
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    const payload = message.payload as
      | { type: string; properties: Record<string, unknown> }
      | undefined;
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    const sessionId = message.sessionId as string | undefined;
    if (!payload?.properties || !sessionId) {
      return;
    }

    const waiter = this.waiters.get(sessionId);
    if (!waiter) {
      return;
    }

    if (payload.type === "message.part.delta") {
      // Text streams as append-only deltas between whole-part events; without
      // this branch, snapshot text only changes when a part completes (the
      // chat router applies the same semantics on its side).
      const deltaProperties =
        messagePartDeltaEventSchema.safeParse(payload).data?.properties;
      if (deltaProperties && deltaProperties.field === "text") {
        this.applyTextDelta({ waiter, properties: deltaProperties });
      }
      return;
    }

    if (
      payload.type === "message.created" ||
      payload.type === "message.updated"
    ) {
      const messageRole = getMessageRole(payload.properties.info);
      if (messageRole) {
        waiter.messageRoles.set(messageRole.messageId, messageRole.role);
        this.publishSnapshot(waiter);
        if (waiter.isStatusCompletionRequested) {
          this.scheduleIdleCompletion(waiter);
        }
      }
      return;
    }

    if (
      payload.type === "message.part.updated" ||
      payload.type === "message.part.added" ||
      payload.type === "message.part.created"
    ) {
      const parsedPart = opencodePartDataSchema.safeParse(
        payload.properties.part,
      );
      if (
        parsedPart.success &&
        (parsedPart.data.type === "text" || parsedPart.data.type === "tool")
      ) {
        if (parsedPart.data.type === "text") {
          this.markSubmittedPromptAsUserText({
            part: parsedPart.data,
            waiter,
          });
        }

        upsertSessionPart({
          part: parsedPart.data,
          partIndices: waiter.partIndices,
          parts: waiter.parts,
        });
        this.publishSnapshot(waiter);
        if (waiter.isStatusCompletionRequested) {
          this.scheduleIdleCompletion(waiter);
        }
      }
      return;
    }

    if (payload.type === "session.idle") {
      if (!hasSessionOutput(waiter)) {
        waiter.isStatusCompletionRequested = true;
        return;
      }
      this.finishNow(waiter);
      return;
    }

    if (payload.type === "session.error") {
      const errorRaw = payload.properties.error;
      const errorMessage =
        typeof errorRaw === "string"
          ? errorRaw
          : JSON.stringify(errorRaw ?? null);
      clearCompletionTimer(waiter);
      const error = new Error(errorMessage);
      for (const subscriber of waiter.subscribers) {
        subscriber.reject(error);
      }
      this.waiters.delete(sessionId);
    }
  }

  cancelStream(sessionId: string, error: Error) {
    const waiter = this.waiters.get(sessionId);
    if (waiter) {
      clearCompletionTimer(waiter);
      for (const subscriber of waiter.subscribers) {
        subscriber.reject(error);
      }
      this.waiters.delete(sessionId);
    }
  }

  completeStream(sessionId: string) {
    const waiter = this.waiters.get(sessionId);
    if (!waiter) {
      return;
    }

    this.scheduleIdleCompletion(waiter);
  }

  rejectAll(error: Error) {
    for (const waiter of this.waiters.values()) {
      clearCompletionTimer(waiter);
      for (const subscriber of waiter.subscribers) {
        subscriber.reject(error);
      }
    }
    this.waiters.clear();
  }
}
